import { useQuery } from '@tanstack/react-query';

import { KEYS_QUERY } from 'common/constants';
import useService from 'common/hook/useService';
import { handleException } from 'modules/account/detail/helpers/utils';

import type { QsChannel, QsClassification, QsGroup, QsProvince } from 'modules/quick-search/types';

/** Cấp 1 — Kênh phân phối (KPP). Toàn hệ thống, không tham số. */
export const useQuickSearchChannels = (enabled = true) => {
  const service = useService();

  return useQuery({
    queryKey: [KEYS_QUERY.qsChannels],
    queryFn: async (): Promise<QsChannel[]> => {
      try {
        if (!service) return [];
        return await service.QuickSearchController_getChannels();
      } catch (error) {
        handleException(error);
        return [];
      }
    },
    enabled: !!service && enabled,
  });
};

/** Cấp 2 — Phân loại hàng hóa (PLHH) theo KPP đã chọn. */
export const useQuickSearchClassifications = (channelId?: number) => {
  const service = useService();

  return useQuery({
    queryKey: [KEYS_QUERY.qsClassifications, channelId],
    queryFn: async (): Promise<QsClassification[]> => {
      try {
        if (!service || !channelId) return [];
        return await service.QuickSearchController_getClassifications({ queries: { channelId } });
      } catch (error) {
        handleException(error);
        return [];
      }
    },
    enabled: !!service && !!channelId,
  });
};

/** Danh mục Tỉnh/Thành phố kèm Phường/Xã — đổ 2 dropdown lọc theo địa bàn (bảng NCC). */
export const useQuickSearchProvinces = (enabled = true) => {
  const service = useService();

  return useQuery({
    queryKey: [KEYS_QUERY.qsProvinces],
    queryFn: async (): Promise<QsProvince[]> => {
      try {
        if (!service) return [];
        return await service.QuickSearchController_getProvinces();
      } catch (error) {
        handleException(error);
        return [];
      }
    },
    enabled: !!service && enabled,
    staleTime: 6 * 60 * 60 * 1000, // danh mục hành chính hiếm đổi
  });
};

/** Cấp 3 — Nhóm mặt hàng (NMH) theo KPP + PLHH đã chọn. */
export const useQuickSearchGroups = (channelId?: number, classificationId?: number) => {
  const service = useService();

  return useQuery({
    queryKey: [KEYS_QUERY.qsGroups, channelId, classificationId],
    queryFn: async (): Promise<QsGroup[]> => {
      try {
        if (!service || !channelId || !classificationId) return [];
        return await service.QuickSearchController_getGroups({
          queries: { channelId, classificationId },
        });
      } catch (error) {
        handleException(error);
        return [];
      }
    },
    enabled: !!service && !!channelId && !!classificationId,
  });
};
